import {
  ErrorCode,
  MessageTypeEnum,
  DashboardConversationFeedItemType,
  type ApiResponse,
  type CreatorHubConversationChannel,
  type DashboardMessageFeedItem,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toaster } from '@/components/ui/toaster';
import {
  conversationMessagesQueryKey,
  type ConversationMessagesInfiniteData,
} from '@/hooks/queries/use-conversation-messages-query';
import {
  appendOptimisticMessage,
  updateOptimisticMessageState,
} from '@/hooks/queries/conversation-cache';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import api from '@/libs/axiosInstance';
import { trackProductAction } from '@/libs/product-analytics';

type SendMessageInput = {
  conversationId: string;
  channel: CreatorHubConversationChannel;
  content: string;
};

type SendMessageResponse = ApiResponse<{ messageId: string }>;

type SendMessageVariables = SendMessageInput & {
  clientMessageId: string;
};

const logicalMessageKey = ({ conversationId, channel, content }: SendMessageInput) =>
  JSON.stringify([conversationId, channel.id, content]);

const sendMessageFn = async (params: SendMessageVariables): Promise<SendMessageResponse> => {
  const response = await api.post(`/dashboard/conversations/${params.conversationId}/messages`, {
    channelId: params.channel.id,
    content: params.content,
    clientMessageId: params.clientMessageId,
  });
  return response.data;
};

export const useSendMessage = (brandId: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pendingClientMessageIds = useRef(new Map<string, string>());

  const mutation = useMutation<SendMessageResponse, AxiosError<ApiResponse>, SendMessageVariables>({
    mutationFn: (params) =>
      trackProductAction('conversations', 'send', () => sendMessageFn(params)),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: conversationQueryKeys.messagesPrefix(brandId, variables.conversationId),
      });
      const now = new Date().toISOString();
      const optimisticMessage: DashboardMessageFeedItem = {
        id: `client:${variables.clientMessageId}`,
        itemType: DashboardConversationFeedItemType.MESSAGE,
        conversationId: variables.conversationId,
        content: variables.content,
        type: MessageTypeEnum.TEXT,
        channel: variables.channel,
        direction: 'brand_to_creator',
        isBrandSender: true,
        clientMessageId: variables.clientMessageId,
        deliveryState: 'pending',
        senderId: '',
        recipientId: '',
        sentAt: now,
        createdAt: now,
        occurredAt: now,
        platformMessageId: null,
        mentionId: null,
        method: null,
        updatedAt: now,
      };

      queryClient.setQueriesData<ConversationMessagesInfiniteData>(
        { queryKey: conversationQueryKeys.messagesPrefix(brandId, variables.conversationId) },
        (previous) => appendOptimisticMessage(previous, optimisticMessage)
      );
    },
    onSuccess: (_, variables) => {
      const key = logicalMessageKey(variables);
      if (pendingClientMessageIds.current.get(key) === variables.clientMessageId) {
        pendingClientMessageIds.current.delete(key);
      }

      queryClient.setQueriesData<ConversationMessagesInfiniteData>(
        { queryKey: conversationQueryKeys.messagesPrefix(brandId, variables.conversationId) },
        (previous) => updateOptimisticMessageState(previous, variables.clientMessageId, 'sent')
      );
      queryClient.invalidateQueries({
        queryKey: conversationMessagesQueryKey(brandId, variables.conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.detail(brandId, variables.conversationId),
      });
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.lists(brandId) });
      queryClient.invalidateQueries({ queryKey: conversationQueryKeys.counters(brandId) });
    },
    onError: (error, variables) => {
      const key = logicalMessageKey(variables);
      if (
        error.response &&
        pendingClientMessageIds.current.get(key) === variables.clientMessageId
      ) {
        pendingClientMessageIds.current.delete(key);
      }
      queryClient.setQueriesData<ConversationMessagesInfiniteData>(
        { queryKey: conversationQueryKeys.messagesPrefix(brandId, variables.conversationId) },
        (previous) => updateOptimisticMessageState(previous, variables.clientMessageId, 'failed')
      );
      // A cadence refusal is not a delivery failure: the message never left, so
      // it gets its own wording. The draft stays in the composer either way.
      if (error.response?.data?.code === ErrorCode.E_RATE_LIMIT_EXCEEDED) {
        toaster.error({
          title: t('messages.send.rateLimited.title'),
          description: t('messages.send.rateLimited.description'),
        });
        return;
      }
      toaster.error({
        title: t('messages.send.error.title'),
        description: error.response?.data?.message ?? t('messages.send.error.description'),
      });
    },
  });
  const { mutateAsync } = mutation;

  const sendMessage = useCallback(
    (input: SendMessageInput) => {
      const key = logicalMessageKey(input);
      const clientMessageId = pendingClientMessageIds.current.get(key) ?? crypto.randomUUID();
      pendingClientMessageIds.current.set(key, clientMessageId);

      return mutateAsync({ ...input, clientMessageId });
    },
    [mutateAsync]
  );

  return { ...mutation, sendMessage };
};
